import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useInView } from 'framer-motion';

const PhilanthropySection = styled.section`
  padding: 80px 10%;
  background-color: var(--color-bg-alt);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 60px 5%;
  }
  
  @media (max-width: 576px) {
    padding: 40px 5%;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--color-text);
  margin-bottom: 1rem;
  text-align: center;
  position: relative;
  font-weight: 600;
  letter-spacing: 0.5px;

  &:after {
    content: '';
    position: absolute;
    width: 50px;
    height: 3px;
    background-color: var(--color-primary);
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }

  @media (max-width: 576px) {
    font-size: 1.8rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--color-secondary);
  text-align: center;
  max-width: 600px;
  margin: 0 auto 3rem;
  font-weight: 300;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin: 0 auto 2.5rem;
  }
  
  @media (max-width: 576px) {
    font-size: 0.9rem;
    margin: 0 auto 2rem;
  }
`;

const PhilanthropyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 1.2rem;
  }
`;

const PhilanthropyCard = styled(motion.div)`
  background: var(--color-card);
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 6px 20px var(--color-shadow);
  transition: all 0.3s ease;
  border: 1px solid transparent;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 35px var(--color-shadow);
    border-color: var(--color-primary);
    
    &::before {
      transform: scaleX(1);
    }
  }
  
  @media (max-width: 576px) {
    padding: 1.5rem;
  }
`;

const PhilanthropyIcon = styled(motion.div)`
  width: 70px;
  height: 70px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: white;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 576px) {
    width: 60px;
    height: 60px;
    font-size: 1.8rem;
    margin-bottom: 1.2rem;
  }
`;

const PhilanthropyTitle = styled.h3`
  font-size: 1.3rem;
  color: var(--color-text);
  margin-bottom: 0.8rem;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
  
  @media (max-width: 576px) {
    font-size: 1.2rem;
  }
`;

const PhilanthropyOrganization = styled.p`
  font-size: 1rem;
  color: var(--color-primary);
  margin-bottom: 0.8rem;
  font-weight: 600;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media (max-width: 576px) {
    font-size: 0.9rem;
  }
`;

const PhilanthropyPeriod = styled.p`
  font-size: 0.9rem;
  color: var(--color-secondary);
  margin-bottom: 1rem;
  font-weight: 500;
  text-align: center;
  
  @media (max-width: 576px) {
    font-size: 0.85rem;
  }
`;

const PhilanthropyDescription = styled.p`
  font-size: 0.95rem;
  color: var(--color-secondary);
  line-height: 1.7;
  margin: 0;
  text-align: left;
  
  @media (max-width: 576px) {
    font-size: 0.9rem;
    line-height: 1.6;
  }
`;

const Philanthropy = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.1 });

  const philanthropyData = [
    {
      id: 1,
      title: 'Founder and Volunteer',
      organization: 'GCP',
      period: '2013 - Present',
      description: 'We raise funds over 150K every year to support underprivileged but talented kids to reach new heights in education, arts and sports.',
      link: null,
      icon: "🤝"
    },
    {
      id: 2,
      title: 'Volunteer',
      organization: 'Benevity',
      period: '2014 - 2023',
      description: 'A CSR program to enrich knowledge of senior citizens on computers and smartphones. I contributed to forestation initiatives at Australian National Parks and awarded for my work.',
      link: 'https://drive.google.com/file/d/12_VADnm9L5km1IMKHg_TjCNn4fZrnXlr/view',
      icon: "🌱"
    },
    {
      id: 3,
      title: 'Volunteer',
      organization: 'Lotus',
      period: '1997 - 2013',
      description: 'Offered to teach English and Science to Govt. school kids during public holidays. Cleaned villages, mentored children and lit eco awareness to old-aged.',
      link: null,
      icon: "📚"
    }
  ];

  return (
    <PhilanthropySection id="philanthropy" ref={sectionRef}>
      <SectionTitle>Philanthropy & Community Service</SectionTitle><br />
      <PhilanthropyGrid>
        {philanthropyData.map((item, index) => (
          <PhilanthropyCard
            key={item.id}
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ 
              duration: 0.6, 
              delay: index * 0.2,
              type: "spring",
              stiffness: 100,
              damping: 15
            }}
            whileHover={{ 
              scale: 1.02,
              transition: { duration: 0.2 }
            }}
          >
            <PhilanthropyIcon
              initial={{ scale: 0, rotate: -180 }}
              animate={isInView ? { scale: 1, rotate: 0 } : {}}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.2 + 0.1,
                type: "spring",
                stiffness: 200,
                damping: 20
              }}
            >
              {item.icon}
            </PhilanthropyIcon>
            <PhilanthropyTitle>{item.title}</PhilanthropyTitle>
            <PhilanthropyOrganization>{item.organization}</PhilanthropyOrganization>
            <PhilanthropyPeriod>{item.period}</PhilanthropyPeriod>
            <PhilanthropyDescription>
              {item.description}<br />
              {item.link && (
                <a 
                  href={item.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  View Certificate
                </a>
              )}
            </PhilanthropyDescription>
          </PhilanthropyCard>
        ))}
      </PhilanthropyGrid>
    </PhilanthropySection>
  );
};

export default Philanthropy;     